import { useState, useEffect } from 'react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { Capability, CAPABILITY_NATURES, CAPABILITY_DOMAINS } from '../types';
import { Modal } from '../components/common/Modal';
import { FileUpload } from '../components/common/FileUpload';
import { Settings, Pencil, Trash2 } from 'lucide-react';
import { useT } from '../i18n';

export function CapabilitiesPage() {
  const { t } = useT();
  const { isAdmin } = useAuth();
  const [capabilities, setCapabilities] = useState<Capability[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [editingCapability, setEditingCapability] = useState<Capability | null>(null);
  const [filterNature, setFilterNature] = useState<string>('');
  const [filterDomain, setFilterDomain] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [deleting, setDeleting] = useState(false);

  const [form, setForm] = useState({
    title: '',
    description: '',
    nature: 'Assess' as Capability['nature'],
    domain: 'Participant' as Capability['domain'],
  });

  useEffect(() => {
    loadCapabilities();
  }, []);

  const loadCapabilities = async () => {
    try {
      const data = await api.getCapabilities() as Capability[];
      setCapabilities(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingCapability) {
        await api.updateCapability(editingCapability.id, form);
      } else {
        await api.createCapability(form);
      }
      setShowModal(false);
      setEditingCapability(null);
      setForm({ title: '', description: '', nature: 'Assess', domain: 'Participant' });
      loadCapabilities();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleEdit = (capability: Capability) => {
    setEditingCapability(capability);
    setForm({
      title: capability.title,
      description: capability.description,
      nature: capability.nature,
      domain: capability.domain,
    });
    setShowModal(true);
  };

  const handleDelete = async (capability: Capability) => {
    if (!confirm(t('cap.confirmDelete').replace('{title}', capability.title))) return;
    try {
      await api.deleteCapability(capability.id);
      loadCapabilities();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleUpload = async (data: any[]) => {
    try {
      const result = await api.uploadCapabilities(data) as { success: boolean; inserted: number; errors: any[] };
      if (result.errors.length > 0) {
        alert(t('cap.uploadResult').replace('{n}', String(result.inserted)).replace('{e}', String(result.errors.length)));
      }
      setShowUpload(false);
      loadCapabilities();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleBulkDelete = async () => {
    const count = selectedIds.size;
    const confirmMsg = count > 1
      ? t('cap.confirmBulkMany').replace('{n}', String(count))
      : t('cap.confirmBulkOne');
    if (!confirm(confirmMsg)) return;

    setDeleting(true);
    try {
      await api.bulkDeleteCapabilities(Array.from(selectedIds));
      setSelectedIds(new Set());
      loadCapabilities();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredCapabilities.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredCapabilities.map((c) => c.id)));
    }
  };

  const toggleSelect = (id: number) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const filteredCapabilities = capabilities.filter((c) => {
    if (filterNature && c.nature !== filterNature) return false;
    if (filterDomain && c.domain !== filterDomain) return false;
    return true;
  });

  // Group by domain
  const groupedByDomain = CAPABILITY_DOMAINS.reduce((acc, domain) => {
    acc[domain] = filteredCapabilities.filter((c) => c.domain === domain);
    return acc;
  }, {} as Record<string, Capability[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: 200 }}>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div>
      <div className="card-header mb-16">
        <div className="flex gap-8">
          <select
            className="form-select"
            style={{ width: 'auto' }}
            value={filterNature}
            onChange={(e) => { setFilterNature(e.target.value); setSelectedIds(new Set()); }}
          >
            <option value="">{t('cap.allNatures')}</option>
            {CAPABILITY_NATURES.map((nature) => (
              <option key={nature} value={nature}>
                {nature}
              </option>
            ))}
          </select>
          <select
            className="form-select"
            style={{ width: 'auto' }}
            value={filterDomain}
            onChange={(e) => { setFilterDomain(e.target.value); setSelectedIds(new Set()); }}
          >
            <option value="">{t('cap.allDomains')}</option>
            {CAPABILITY_DOMAINS.map((domain) => (
              <option key={domain} value={domain}>
                {domain}
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-8">
          {isAdmin && selectedIds.size > 0 && (
            <button
              className="btn btn-danger"
              onClick={handleBulkDelete}
              disabled={deleting}
            >
              {deleting ? t('cap.deleting') : t('cap.deleteCount').replace('{n}', String(selectedIds.size))}
            </button>
          )}
          {isAdmin && (
            <>
              <button className="btn btn-ghost" onClick={() => setShowUpload(true)}>
                {t('csv.uploadCsv')}
              </button>
              <button
                className="btn btn-accent"
                onClick={() => {
                  setEditingCapability(null);
                  setForm({ title: '', description: '', nature: 'Assess', domain: 'Participant' });
                  setShowModal(true);
                }}
              >
                {t('cap.addCapability')}
              </button>
            </>
          )}
        </div>
      </div>

      {filteredCapabilities.length === 0 ? (
        <div className="empty-state-card">
          <div className="empty-state">
            <div className="empty-state-icon"><Settings /></div>
            <div className="empty-state-title">{t('cap.emptyTitle')}</div>
            <div className="empty-state-desc">
              {isAdmin ? t('cap.emptyDescAdmin') : t('cap.emptyDescUser')}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-16">
          {CAPABILITY_DOMAINS.map((domain) => {
            const domainCapabilities = groupedByDomain[domain];
            if (domainCapabilities.length === 0) return null;

            return (
              <div key={domain} className="card">
                <h3 className="mb-12">{domain}</h3>{/* domain: DB enum value, not translated */}
                <table className="table">
                  <thead>
                    <tr>
                      {isAdmin && (
                        <th style={{ width: 40 }}>
                          <input
                            type="checkbox"
                            checked={domainCapabilities.length > 0 && domainCapabilities.every((c) => selectedIds.has(c.id))}
                            onChange={() => {
                              const allSelected = domainCapabilities.every((c) => selectedIds.has(c.id));
                              const newSet = new Set(selectedIds);
                              domainCapabilities.forEach((c) => {
                                if (allSelected) {
                                  newSet.delete(c.id);
                                } else {
                                  newSet.add(c.id);
                                }
                              });
                              setSelectedIds(newSet);
                            }}
                            title={t('cap.selectAllIn').replace('{domain}', domain)}
                          />
                        </th>
                      )}
                      <th>{t('cap.colTitle')}</th>
                      <th>{t('cap.colNature')}</th>
                      <th>{t('cap.colDescription')}</th>
                      {isAdmin && <th></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {domainCapabilities.map((capability) => (
                      <tr key={capability.id}>
                        {isAdmin && (
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedIds.has(capability.id)}
                              onChange={() => toggleSelect(capability.id)}
                            />
                          </td>
                        )}
                        <td className="font-medium">{capability.title}</td>
                        <td>
                          <span className="badge badge-accent">{capability.nature}</span>
                        </td>
                        <td className="text-muted">
                          {capability.description?.slice(0, 100)}
                          {capability.description?.length > 100 ? '...' : ''}
                        </td>
                        {isAdmin && (
                          <td>
                            <div className="flex gap-8">
                              <button className="btn-icon" onClick={() => handleEdit(capability)} title={t('common.edit')}>
                                <Pencil className="icon-sm" />
                              </button>
                              <button className="btn-icon" onClick={() => handleDelete(capability)} title={t('common.delete')}>
                                <Trash2 className="icon-sm" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <Modal
          title={editingCapability ? t('cap.editCapability') : t('cap.addCapability')}
          onClose={() => {
            setShowModal(false);
            setEditingCapability(null);
          }}
        >
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">{t('cap.labelTitle')}</label>
              <input
                type="text"
                className="form-input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('cap.labelNature')}</label>
              <select
                className="form-select"
                value={form.nature}
                onChange={(e) => setForm({ ...form, nature: e.target.value as Capability['nature'] })}
                required
              >
                {CAPABILITY_NATURES.map((nature) => (
                  <option key={nature} value={nature}>
                    {nature}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t('cap.labelDomain')}</label>
              <select
                className="form-select"
                value={form.domain}
                onChange={(e) => setForm({ ...form, domain: e.target.value as Capability['domain'] })}
                required
              >
                {CAPABILITY_DOMAINS.map((domain) => (
                  <option key={domain} value={domain}>
                    {domain}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t('cap.labelDescription')}</label>
              <textarea
                className="form-textarea"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="modal-footer" style={{ padding: 0, border: 'none', marginTop: 'var(--s16)' }}>
              <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="btn btn-accent">
                {editingCapability ? t('common.save') : t('cap.create')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {showUpload && (
        <Modal title={t('cap.uploadModalTitle')} onClose={() => setShowUpload(false)}>
          <FileUpload
            expectedHeaders={['Title', 'Description', 'Nature', 'Domain']}
            onUpload={handleUpload}
            onCancel={() => setShowUpload(false)}
          />
        </Modal>
      )}
    </div>
  );
}
